import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { directAuth } from '../../services/directAuth';
import GlobalNavBar from '../layout/GlobalNavBar';

interface ResetPasswordFormData {
  verificationCode: string;
  newPassword: string;
  confirmPassword: string;
}

interface LocationState {
  email?: string;
}

const REDIRECT_DELAY_MS = 3000;
const RESEND_COOLDOWN_SECONDS = 60;

export const ResetPasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('auth');

  const email = (location.state as LocationState | null)?.email;

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ResetPasswordFormData>();

  const watchNewPassword = watch('newPassword');

  // Guard: if the email wasn't passed via route state, bounce back to the
  // forgot-password entry point.
  useEffect(() => {
    if (!email) {
      navigate('/forgot-password', { replace: true });
    }
  }, [email, navigate]);

  // On success, redirect to login after 3 seconds.
  useEffect(() => {
    if (!isSuccess) return;
    const timer = window.setTimeout(() => {
      navigate('/login', { replace: true });
    }, REDIRECT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [isSuccess, navigate]);

  // Tick down the resend cooldown once per second while > 0.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = window.setInterval(() => {
      setResendCooldown((seconds) => (seconds > 0 ? seconds - 1 : 0));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [resendCooldown]);

  const handleResendCode = async () => {
    if (!email || isResending || resendCooldown > 0) return;
    setIsResending(true);
    setApiError(null);
    try {
      const response = await directAuth.forgotPassword(email);
      if (response.success) {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        toast.success(t('auth:resendResetCodeSuccess'));
      } else {
        const key = response.errorCode
          ? `auth:resetErrors.${response.errorCode}`
          : 'auth:resendResetCodeFailed';
        const message = t(key, {
          defaultValue: t('auth:resendResetCodeFailed'),
        });
        setApiError(message);
        toast.error(message);
      }
    } catch (error: any) {
      console.error('Resend reset code error:', error);
      const message = t('auth:resendResetCodeFailed');
      setApiError(message);
      toast.error(message);
    } finally {
      setIsResending(false);
    }
  };

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!email) return;
    setIsLoading(true);
    setApiError(null);
    try {
      const response = await directAuth.resetPassword(
        email,
        data.verificationCode,
        data.newPassword,
      );

      if (response.success) {
        reset();
        setIsSuccess(true);
        toast.success(t('auth:passwordResetSuccessTitle'));
      } else {
        const key = response.errorCode
          ? `auth:resetErrors.${response.errorCode}`
          : 'auth:errors.resetPasswordFailed';
        const message = t(key, {
          defaultValue: t('auth:errors.resetPasswordFailed'),
        });
        setApiError(message);
        toast.error(message);
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      const message = t('auth:errors.resetPasswordFailed');
      setApiError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render the form before the redirect-to-forgot-password effect runs.
  if (!email) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNavBar variant="dark" />

      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {isSuccess
                ? t('auth:passwordResetSuccessTitle')
                : t('auth:resetPasswordTitle')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {isSuccess
                ? t('auth:passwordResetSuccessDesc')
                : t('auth:resetPasswordDesc')}
            </p>
          </div>

          {isSuccess ? (
            <div className="mt-8 space-y-6">
              <div
                className="p-4 bg-green-50 border border-green-200 rounded-md"
                role="status"
                aria-live="polite"
              >
                <p className="text-sm text-green-700">
                  {t('auth:passwordResetSuccessDesc')}
                </p>
              </div>
              <div className="text-center">
                <Link
                  to="/login"
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  {t('auth:backToLogin')}
                </Link>
              </div>
            </div>
          ) : (
            <form
              className="mt-8 space-y-6"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <div>
                <label
                  htmlFor="verificationCode"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('auth:verificationCode')}
                </label>
                <input
                  id="verificationCode"
                  {...register('verificationCode', {
                    required: t('auth:errors.verificationCodeRequired'),
                    pattern: {
                      value: /^\d{6}$/,
                      message: t('auth:errors.verificationCodeFormat'),
                    },
                  })}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  disabled={isLoading}
                  aria-invalid={errors.verificationCode ? 'true' : 'false'}
                  aria-describedby={
                    errors.verificationCode ? 'verificationCode-error' : undefined
                  }
                  className="mt-1 input"
                  placeholder={t('auth:enter6DigitCode')}
                />
                {errors.verificationCode && (
                  <p
                    id="verificationCode-error"
                    className="mt-1 text-sm text-red-600"
                    role="alert"
                  >
                    {errors.verificationCode.message}
                  </p>
                )}
              </div>

              {/* Resend verification code button */}
              <div className="flex items-center justify-between -mt-2">
                <p className="text-sm text-gray-500">
                  {t('auth:didntReceiveCode')}
                </p>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isLoading || isResending || resendCooldown > 0}
                  aria-label={t('auth:resendResetCode')}
                  className="text-sm font-medium text-primary-600 hover:text-primary-500 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {isResending
                    ? t('auth:resendResetCodeSending')
                    : resendCooldown > 0
                    ? t('auth:resendResetCodeCooldown', {
                        seconds: resendCooldown,
                      })
                    : t('auth:resendResetCode')}
                </button>
              </div>

              <div className="relative">
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('auth:newPassword')}
                </label>
                <input
                  id="newPassword"
                  {...register('newPassword', {
                    required: t('auth:errors.newPasswordRequired'),
                    minLength: {
                      value: 8,
                      message: t('auth:errors.passwordMinLength'),
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                      message: t('auth:errors.passwordComplexity'),
                    },
                  })}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  disabled={isLoading}
                  aria-invalid={errors.newPassword ? 'true' : 'false'}
                  aria-describedby={
                    errors.newPassword ? 'newPassword-error' : undefined
                  }
                  className="mt-1 input pr-10"
                  placeholder={t('auth:enterNewPassword')}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  style={{ top: '1.75rem' }}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={
                    showPassword
                      ? t('auth:hidePassword')
                      : t('auth:showPassword')
                  }
                  aria-pressed={showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {errors.newPassword && (
                  <p
                    id="newPassword-error"
                    className="mt-1 text-sm text-red-600"
                    role="alert"
                  >
                    {errors.newPassword.message}
                  </p>
                )}
              </div>

              <div className="relative">
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('auth:confirmPassword')}
                </label>
                <input
                  id="confirmPassword"
                  {...register('confirmPassword', {
                    required: t('auth:errors.confirmPasswordRequired'),
                    validate: (value) =>
                      value === watchNewPassword ||
                      t('auth:errors.passwordMismatch'),
                  })}
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  disabled={isLoading}
                  aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                  aria-describedby={
                    errors.confirmPassword ? 'confirmPassword-error' : undefined
                  }
                  className="mt-1 input pr-10"
                  placeholder={t('auth:confirmNewPassword')}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  style={{ top: '1.75rem' }}
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={
                    showConfirmPassword
                      ? t('auth:hidePassword')
                      : t('auth:showPassword')
                  }
                  aria-pressed={showConfirmPassword}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {errors.confirmPassword && (
                  <p
                    id="confirmPassword-error"
                    className="mt-1 text-sm text-red-600"
                    role="alert"
                  >
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>

              {apiError && (
                <div
                  className="p-3 bg-red-50 border border-red-200 rounded-md"
                  role="alert"
                  aria-live="assertive"
                >
                  <p className="text-sm text-red-600">{apiError}</p>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  aria-busy={isLoading}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading
                    ? t('auth:resettingPassword')
                    : t('auth:resetPasswordButton')}
                </button>
              </div>

              <div className="text-center">
                <Link
                  to="/login"
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  {t('auth:backToLogin')}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
